import { defineStore } from 'pinia';
import { api, TOKEN_STORAGE_KEY } from '@/api/client';
import type { AppUser } from '@/types';

interface AuthState {
    token: string | null;
    user: AppUser | null;
    booted: boolean;
}

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        token: localStorage.getItem(TOKEN_STORAGE_KEY),
        user: null,
        booted: false,
    }),

    getters: {
        isAuthenticated: (state) => state.token !== null && state.user !== null,
    },

    actions: {
        async bootstrap(): Promise<void> {
            if (!this.token) {
                this.booted = true;
                return;
            }
            try {
                await this.fetchMe();
            } catch {
                // Token inválido — limpamos para evitar loop de 401.
                this.logoutLocally();
            } finally {
                this.booted = true;
            }
        },

        async login(email: string, password: string): Promise<void> {
            const { data } = await api.post<{ token: string; user: AppUser }>('/login', { email, password });
            this.setSession(data.token, data.user);
        },

        async register(payload: {
            name: string;
            email: string;
            password: string;
            password_confirmation: string;
            phone?: string | null;
            city_id?: number | null;
            terms_accepted: boolean;
        }): Promise<void> {
            const { data } = await api.post<{ token: string; user: AppUser }>('/register', payload);
            this.setSession(data.token, data.user);
        },

        async fetchMe(): Promise<void> {
            const { data } = await api.get<{ data: AppUser }>('/me');
            this.user = data.data;
        },

        async updateMe(patch: Partial<Pick<AppUser, 'name' | 'email' | 'phone'> & { city_id: number | null }>): Promise<void> {
            const { data } = await api.put<{ data: AppUser }>('/me', patch);
            this.user = data.data;
        },

        async updateLocation(lat: number, lng: number): Promise<void> {
            await api.patch('/me/location', { lat, lng });
            if (this.user) {
                this.user.last_lat = lat;
                this.user.last_lng = lng;
            }
        },

        async logout(): Promise<void> {
            try {
                await api.post('/logout');
            } catch {
                // Ignora — vamos limpar localmente de qualquer forma.
            }
            this.logoutLocally();
        },

        setSession(token: string, user: AppUser): void {
            this.token = token;
            this.user = user;
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
        },

        logoutLocally(): void {
            this.token = null;
            this.user = null;
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        },
    },
});
